import { useRef, useState } from 'react'
import type { DragEvent } from 'react'

const DEFAULT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp'

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Append picked files, de-duping by name+size so re-picking doesn't double up. */
export function mergeFiles(existing: File[], incoming: FileList | File[]): File[] {
  const seen = new Set(existing.map((f) => `${f.name}:${f.size}`))
  const next = [...existing]
  for (const f of Array.from(incoming)) {
    const key = `${f.name}:${f.size}`
    if (!seen.has(key)) {
      seen.add(key)
      next.push(f)
    }
  }
  return next
}

export interface FileDropzoneProps {
  /** Controlled list of staged files. */
  files: File[]
  onChange: (files: File[]) => void
  /** `accept` attr for the hidden input. Defaults to PDF/PNG/JPG/WebP. */
  accept?: string
  /** Small hint line under the file list. */
  hint?: string
  /** Prefix for data-testids: `${testIdPrefix}-dropzone`, `-input`, `-list`, `-remove`. */
  testIdPrefix?: string
}

/**
 * Modern drag-and-drop multi-file uploader (client-side staging only — the
 * parent flushes the files after its record exists). Shared by the HR
 * out-of-system encode form and the capacity-change record form.
 */
export function FileDropzone({
  files,
  onChange,
  accept = DEFAULT_ACCEPT,
  hint,
  testIdPrefix = 'file',
}: FileDropzoneProps): JSX.Element {
  const [dragActive, setDragActive] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault()
    setDragActive(false)
    if (e.dataTransfer.files.length > 0) onChange(mergeFiles(files, e.dataTransfer.files))
  }

  function removeFile(name: string, size: number): void {
    onChange(files.filter((f) => !(f.name === name && f.size === size)))
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragActive(true)
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        data-testid={`${testIdPrefix}-dropzone`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '28px 20px',
          borderRadius: 12,
          border: `2px dashed ${dragActive ? 'var(--primary)' : 'var(--border)'}`,
          background: dragActive ? 'var(--accent-soft, var(--surface))' : 'var(--surface)',
          color: 'var(--text-2)',
          cursor: 'pointer',
          textAlign: 'center',
          transition: 'border-color 120ms ease, background 120ms ease',
        }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ color: dragActive ? 'var(--primary)' : 'var(--text-3)' }}
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div style={{ fontSize: 13, color: 'var(--text)' }}>
          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>Click to upload</span> or drag
          and drop
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          PDF, PNG, JPG or WebP · up to 5 MB each · multiple allowed
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          onChange={(e) => {
            if (e.target.files) onChange(mergeFiles(files, e.target.files))
            e.target.value = '' // allow re-picking the same file
          }}
          style={{ display: 'none' }}
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      {files.length > 0 ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}
          data-testid={`${testIdPrefix}-list`}
        >
          {files.map((f) => (
            <div
              key={`${f.name}-${f.size}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ color: 'var(--text-3)', flexShrink: 0 }}
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span
                style={{
                  flex: 1,
                  fontSize: 12.5,
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {f.name}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--text-3)', flexShrink: 0 }}>
                {formatBytes(f.size)}
              </span>
              <button
                type="button"
                onClick={() => removeFile(f.name, f.size)}
                aria-label={`Remove ${f.name}`}
                data-testid={`${testIdPrefix}-remove`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'var(--text-3)',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {hint ? (
        <span style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginTop: 6 }}>
          {hint}
        </span>
      ) : null}
    </div>
  )
}
